import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import {
  RequestFormFooter,
  RequestHistoryOverlay,
  DS_QUICK_FIELD_CLS,
  PHONE_SHEET_BACKDROP_CLS,
  PHONE_SHEET_PANEL_CLS,
  usePhoneFormBack,
} from "./RequestHistoryOverlay";
import { loadQuickActionRequests } from "../../../../lib/attendanceDeskRequests";
import {
  applyRegulariseDateChange,
  applyRegulariseTimeChange,
  clampDateIsoToToday,
  computeDurationBetweenTimeInputs,
  dateTimeFromIsoAndTime,
  getLoggedInEmployeeId,
  getMaxDateInputValue,
  isFutureDateIso,
  loadRegularisationSessionHistory,
  resolveAttendanceDisplayTimeZone,
  resolveEmployeeDayChecks,
  saveRegularisationSessionEntry,
  toTimeInputValue,
  validateRegulariseCheckTimes,
} from "../../../../lib/attendanceDayChecks";
import {
  resolveCompanyMinDateValue,
  resolveEmployeeMinDateValue,
} from "../../../../utils/selectableDateBounds";
import DatePicker from "../../../Common/DatePicker";
import TwelveHourClockTimeField from "./TwelveHourClockTimeField";

const HISTORY_UPDATED_EVENT = "hrms-regularisation-history-updated";

function regulariseDayIso(row) {
  return `${row.year}-${String(Number(row.month) + 1).padStart(2, "0")}-${String(row.day).padStart(2, "0")}`;
}

function resolveRegulariseHistoryStatus(row, employeeId) {
  const dateIso = regulariseDayIso(row);
  const match = loadQuickActionRequests().find((r) => {
    if (String(r.actionLabel || "").toLowerCase() !== "regularise checks") return false;
    if (employeeId && String(r.employeeId) !== String(employeeId)) return false;
    return String(r.formData?.date || r.formData?.dateIso || "").slice(0, 10) === dateIso;
  });
  return match?.status || row.status || "Pending";
}

const emptyDayRecords = () => ({
  loading: false,
  hasRecords: false,
  checkIn: null,
  checkOut: null,
  totalHours: null,
  sources: [],
});

/**
 * Shared Regularise checks request form (Sidebar ESS + attendance calendar).
 */
export default function RegulariseChecksRequestModal({
  isOpen,
  onClose,
  darkMode = false,
  employeeId,
  employeeName = "Employee",
  /** Optional joining/DOJ fields for min date = max(SC, DOJ) */
  joiningDate = null,
  initialDateIso = null,
  loggedInEmployeeId: loggedInEmployeeIdProp = null,
  onSubmit,
  submitLabel = "Submit Request",
  zIndexClass = "z-[11050]",
  embedded = false,
}) {
  const maxDateToday = useMemo(() => getMaxDateInputValue(), [isOpen]);
  const minDateValue = useMemo(
    () =>
      resolveEmployeeMinDateValue(
        joiningDate ? { joiningDate } : null
      ) ||
      resolveCompanyMinDateValue() ||
      undefined,
    [joiningDate, isOpen]
  );
  const [dateIso, setDateIso] = useState("");
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState("");
  const [dayRecords, setDayRecords] = useState(emptyDayRecords);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  /** Only auto-fill In/Out once per selected date (avoids input flicker on refresh). */
  const autoFilledForDateRef = useRef("");
  const timesTouchedRef = useRef(false);

  const loggedInEmployeeId = loggedInEmployeeIdProp ?? getLoggedInEmployeeId();

  const dateLabel = useMemo(() => {
    if (!dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return "-";
    const [y, m, d] = dateIso.split("-");
    return `${d}-${m}-${y}`;
  }, [dateIso]);

  const previewHours = useMemo(
    () => computeDurationBetweenTimeInputs(inTime, outTime),
    [inTime, outTime]
  );

  const historyRows = useMemo(() => {
    if (!employeeId) return [];
    return loadRegularisationSessionHistory(employeeId).map((row) => ({
      ...row,
      status: resolveRegulariseHistoryStatus(row, employeeId),
    }));
  }, [employeeId, historyRefresh, isOpen]);

  const overlayRows = useMemo(
    () =>
      historyRows.map((row) => ({
        id: `${row.year}-${row.month}-${row.day}-${row.id}`,
        title: row.label,
        details: [
          { label: "In", value: row.checkIn ?? "—" },
          { label: "Out", value: row.checkOut ?? "—" },
          { label: "Hrs", value: row.totalHours ?? "—" },
        ],
        status: row.status || "Pending",
      })),
    [historyRows]
  );

  const bumpHistory = () => setHistoryRefresh((n) => n + 1);

  const applyDayCheckResult = (result, { fillTimes, forDateIso }) => {
    const targetDate = forDateIso || dateIso;
    setDayRecords({ loading: false, ...result });
    if (!fillTimes || !result.hasRecords || timesTouchedRef.current) return;
    if (autoFilledForDateRef.current === targetDate) return;
    const effective = clampDateIsoToToday(targetDate);
    const cinRaw = toTimeInputValue(result.checkIn) || "";
    const coutRaw = toTimeInputValue(result.checkOut) || "";
    const { time: cin, error: cinErr } = applyRegulariseTimeChange(
      effective,
      cinRaw,
      "Check-in"
    );
    const { time: cout, error: coutErr } = applyRegulariseTimeChange(
      effective,
      coutRaw,
      "Check-out"
    );
    setInTime(cin);
    setOutTime(cout);
    autoFilledForDateRef.current = targetDate;
    if (cinErr || coutErr) {
      setFormError(cinErr || coutErr || "");
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const initial = initialDateIso ? clampDateIsoToToday(initialDateIso) : maxDateToday;
    setDateIso(initial);
    setInTime("");
    setOutTime("");
    setReason("");
    setNotes("");
    setFormError("");
    setDayRecords(emptyDayRecords());
    setShowHistoryModal(false);
    autoFilledForDateRef.current = "";
    timesTouchedRef.current = false;
    // Reset only when the modal opens — not when parent re-renders draft/date props.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-only reset
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !employeeId || !dateIso) return;
    if (isFutureDateIso(dateIso)) {
      setDayRecords(emptyDayRecords());
      return;
    }
    let cancelled = false;
    const requestDate = dateIso;
    // Only show loading chrome on first load for this date (never on background refresh).
    setDayRecords((prev) =>
      prev.hasRecords || prev.checkIn || prev.checkOut
        ? prev
        : { ...prev, loading: true }
    );
    const preferMyHistory =
      loggedInEmployeeId != null && String(employeeId) === String(loggedInEmployeeId);
    resolveEmployeeDayChecks(String(employeeId), requestDate, { preferMyHistory }).then((result) => {
      if (cancelled) return;
      applyDayCheckResult(result, { fillTimes: true, forDateIso: requestDate });
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeId, dateIso, loggedInEmployeeId]);

  useEffect(() => {
    if (!isOpen || !employeeId) return;
    // Do not re-fetch day records on every punch/tick — that made "Updating…" blink
    // while the navbar work-hours timer runs. History list can still refresh.
    const onHistory = (e) => {
      const id = String(e?.detail?.employeeId ?? "");
      if (!id || id === String(employeeId)) bumpHistory();
    };
    window.addEventListener(HISTORY_UPDATED_EVENT, onHistory);
    return () => {
      window.removeEventListener(HISTORY_UPDATED_EVENT, onHistory);
    };
  }, [isOpen, employeeId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = clampDateIsoToToday(dateIso || maxDateToday);
    const validation = validateRegulariseCheckTimes({
      dateIso: effectiveDate,
      inTime,
      outTime,
    });
    if (!validation.valid) {
      setFormError(validation.errors[0] || "Invalid check times.");
      return;
    }
    const reasonTrim = reason.trim();
    if (!reasonTrim) {
      setFormError("Please enter a reason for this request.");
      return;
    }
    setFormError("");

    const parts = effectiveDate.split("-");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const totalHours = previewHours || "";

    if (employeeId && Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      saveRegularisationSessionEntry(employeeId, {
        dateIso: effectiveDate,
        year,
        month,
        day,
        checkIn: inTime.trim() || "--",
        checkOut: outTime.trim() || "--",
        totalHours,
        status: "Pending",
        reason: reasonTrim,
        notes: notes.trim(),
      });
      bumpHistory();
    }

    // Wall-clock as typed → absolute ISO in the submitter’s zone (IST if unknown).
    // Backend must not re-interpret HH:mm in server TZ (that caused 09:05 → 14:35).
    const timeZone = resolveAttendanceDisplayTimeZone();
    const inWall = inTime.trim();
    const outWall = outTime.trim();
    const inDt = dateTimeFromIsoAndTime(effectiveDate, inWall);
    const outDt = dateTimeFromIsoAndTime(effectiveDate, outWall);
    onSubmit?.({
      date: effectiveDate,
      inTime: inWall,
      outTime: outWall,
      checkInTime: inDt && !Number.isNaN(inDt.getTime()) ? inDt.toISOString() : inWall,
      checkOutTime: outDt && !Number.isNaN(outDt.getTime()) ? outDt.toISOString() : outWall,
      timeZone,
      reason: reasonTrim,
      notes: notes.trim(),
      totalHours,
      year,
      month,
      day,
    });
  };

  usePhoneFormBack(isOpen && !embedded, onClose);

  if (!isOpen) return null;

  const inputCls = DS_QUICK_FIELD_CLS;

  const shellCls =
    "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  const formBody = (
    <form
      onSubmit={handleSubmit}
      className={`space-y-3 [scrollbar-gutter:stable] ${
        embedded
          ? "min-h-0 flex-1 overflow-y-auto"
          : "max-h-[min(82vh,720px)] overflow-y-auto p-4 max-md:max-h-none max-md:min-h-0 max-md:flex-1"
      }`}
    >
            <p className={`text-[10px] ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              You cannot regularise a future date or future check-in/out time. Choose a date to see
              existing punches for the employee, then enter corrected times.
            </p>

            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Date
              <DatePicker
                required
                portal
                darkMode={darkMode}
                min={minDateValue}
                max={maxDateToday}
                value={dateIso || maxDateToday}
                dropdownClassName="z-[13000]"
                className={inputCls}
                onChange={(e) => {
                  const { dateIso: nextDate, error } = applyRegulariseDateChange(
                    e?.target?.value ?? ""
                  );
                  setFormError(error || "");
                  autoFilledForDateRef.current = "";
                  timesTouchedRef.current = false;
                  setInTime("");
                  setOutTime("");
                  setDateIso(nextDate);
                }}
              />
            </label>

            <div className="min-h-[1.25rem]" aria-live="polite">
              {formError ? (
                <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                  {formError}
                </p>
              ) : null}
            </div>

            <div
              className={`relative min-h-[6.75rem] rounded-xl border px-3 py-2 text-xs ${
                dayRecords.hasRecords
                  ? darkMode
                    ? "border-emerald-900 bg-emerald-950/30 text-emerald-100"
                    : "border-emerald-200 bg-emerald-50/80 text-emerald-900"
                  : darkMode
                  ? "border-[#3C3C3C] bg-[#1a1a1a] text-gray-400"
                  : "border-gray-200 bg-gray-50 text-gray-600"
              }`}
            >
              {dayRecords.loading && !dayRecords.hasRecords ? (
                <p className="text-[hsl(var(--ds-muted-fg))]">
                  Loading check-in/out for {dateLabel}…
                </p>
              ) : dayRecords.hasRecords ? (
                <>
                  <p className="font-semibold">
                    These are the checks as per the records for the day ({dateLabel}):
                  </p>
                  <ul className="mt-1.5 space-y-0.5 tabular-nums">
                    <li>
                      <span className="font-medium">Check-in:</span> {dayRecords.checkIn ?? "—"}
                    </li>
                    <li>
                      <span className="font-medium">Check-out:</span> {dayRecords.checkOut ?? "—"}
                    </li>
                    <li>
                      <span className="font-medium">Total hours:</span>{" "}
                      {dayRecords.totalHours || "—"}
                    </li>
                  </ul>
                  <p className="mt-1 min-h-[1rem] text-[10px] opacity-80">
                    {dayRecords.sources?.length > 0
                      ? `Source: ${
                          dayRecords.sources.includes("system") &&
                          dayRecords.sources.includes("punch")
                            ? "System records and navbar punch"
                            : dayRecords.sources.includes("system")
                            ? "System attendance records"
                            : "Navbar check-in/out"
                        }`
                      : "\u00a0"}
                  </p>
                </>
              ) : (
                <p>
                  No check-in/out records found in the system for {dateLabel}. Enter corrected times
                  below.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                <label htmlFor="regularise-in-hour">In Time</label>
                <TwelveHourClockTimeField
                  id="regularise-in-hour"
                  required
                  ariaLabel="Check-in"
                  value={inTime}
                  inputClassName={inputCls}
                  onChange={(next) => {
                    timesTouchedRef.current = true;
                    const effective = clampDateIsoToToday(dateIso || maxDateToday);
                    const { time, error } = applyRegulariseTimeChange(
                      effective,
                      next,
                      "Check-in"
                    );
                    setFormError(error || "");
                    setInTime(time);
                  }}
                />
              </div>
              <div className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                <label htmlFor="regularise-out-hour">Out Time</label>
                <TwelveHourClockTimeField
                  id="regularise-out-hour"
                  required
                  ariaLabel="Check-out"
                  value={outTime}
                  inputClassName={inputCls}
                  onChange={(next) => {
                    timesTouchedRef.current = true;
                    const effective = clampDateIsoToToday(dateIso || maxDateToday);
                    const { time, error } = applyRegulariseTimeChange(
                      effective,
                      next,
                      "Check-out"
                    );
                    setFormError(error || "");
                    setOutTime(time);
                  }}
                />
              </div>
            </div>

            <p className={`min-h-[1rem] text-[10px] ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Total hours (reference):{" "}
              <span className={`font-semibold tabular-nums ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                {!inTime.trim() || !outTime.trim() ? "—" : previewHours || "—"}
              </span>
            </p>

            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Reason
              <textarea
                required
                rows={3}
                value={reason}
                onChange={(e) => {
                  setFormError("");
                  setReason(e.target.value);
                }}
                className={inputCls}
                placeholder="Enter request reason..."
              />
            </label>

            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Notes (optional)
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputCls}
                placeholder="Additional details"
              />
            </label>

            <RequestFormFooter
              historyCount={historyRows.length}
              onViewHistory={() => setShowHistoryModal(true)}
              onCancel={onClose}
              submitDisabled={!!formError}
              submitLabel={submitLabel}
            />
          </form>
  );

  const historyOverlay = (
    <RequestHistoryOverlay
      open={showHistoryModal}
      onClose={() => setShowHistoryModal(false)}
      title="Regularise checks application history"
      subtitle={`${employeeName}${employeeId ? ` · ID ${employeeId}` : ""}`}
      rows={overlayRows}
      emptyMessage="No regularised check dates yet. Submit a request to add an entry here."
    />
  );

  if (embedded) {
    return (
      <>
        <div className="flex h-full min-h-0 flex-col">{formBody}</div>
        {historyOverlay}
      </>
    );
  }

  return createPortal(
    <>
      <div
        className={`fixed inset-0 ${zIndexClass} flex items-start justify-center overflow-y-auto bg-black/45 p-4 pt-20 sm:pt-10 max-md:overflow-hidden ${PHONE_SHEET_BACKDROP_CLS}`}
        role="presentation"
        onClick={onClose}
      >
        <div
          className={`my-2 w-full max-w-xl shrink-0 rounded-2xl border shadow-[var(--ds-shadow-card)] max-md:my-0 max-md:flex max-md:flex-col ${PHONE_SHEET_PANEL_CLS} ${shellCls}`}
          role="dialog"
          aria-labelledby="regularise-checks-request-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              darkMode ? "border-[#3C3C4C]" : "border-gray-200"
            }`}
          >
            <div className="min-w-0 pr-2">
              <h3
                id="regularise-checks-request-title"
                className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}
              >
                Regularise checks Request
              </h3>
              <p className={`mt-0.5 truncate text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                Employee: {employeeName}
                {employeeId ? ` (ID: ${employeeId})` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-1.5 shrink-0 text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close regularise checks"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {formBody}
        </div>
      </div>
      {historyOverlay}
    </>,
    document.body
  );
}
